import type { CSSProperties, ReactNode } from 'react';

type RevealMode = 'fade-up' | 'fade-down' | 'fade-left' | 'fade-right' | 'scale' | 'blur';

/**
 * Scroll-triggered reveal, driven by one page-level IntersectionObserver and a
 * CSS transition.
 *
 * This used to be a framer-motion component. The easing was already a plain
 * cubic-bezier — [0.21, 0.47, 0.32, 0.98] — so CSS reproduces the motion exactly;
 * nothing about the feel changes. What changes is where the work happens.
 *
 * framer-motion drove each reveal from JavaScript, writing styles every frame,
 * which is why Lighthouse reported all of them as non-composited ("Effect has
 * unsupported timing parameters"). With 110 usages across 26 files that was a
 * large share of the main thread — 2,361 ms of Script Evaluation and a TBT of
 * 845 ms on mobile, and TBT is the lab proxy for INP, a ranking signal.
 *
 * A CSS transition on opacity/transform runs on the compositor instead, so these
 * cost the main thread nothing once started. The observer also used to live in
 * every instance: the homepage created more than one hundred React effects,
 * refs, state cells, and client-component boundaries for otherwise static
 * wrappers. ScrollReveal is now server-renderable and ScrollRevealObserver owns
 * the single shared observer. Trello M-06 / P-04 / P-06.
 *
 * The props API is unchanged, so all 110 call sites work as-is.
 */

const HIDDEN_TRANSFORM: Record<RevealMode, string> = {
    'fade-up': 'translateY(40px)',
    'fade-down': 'translateY(-40px)',
    'fade-left': 'translateX(40px)',
    'fade-right': 'translateX(-40px)',
    scale: 'scale(0.8)',
    blur: 'none',
};

/** Matches the easing the framer-motion version used, so the motion is identical. */
const EASE = 'cubic-bezier(0.21, 0.47, 0.32, 0.98)';

interface ScrollRevealProps {
    children: ReactNode;
    mode?: RevealMode;
    delay?: number;
    duration?: number;
    className?: string;
    threshold?: number;
    once?: boolean;
    /**
     * For content already on screen at first paint — the hero, chiefly.
     *
     * The hidden state is server-rendered, so without this the element ships at
     * opacity 0 and stays invisible until hydration. An element at opacity 0
     * cannot be the Largest Contentful Paint, which is what held the hero
     * headline's LCP at 2,820 ms of render delay. Trello P-01.
     *
     * With `eager` the element ships opaque and paints straight from the server
     * HTML, and still slides in — LCP counts a transformed element as painted,
     * only opacity hides it.
     */
    eager?: boolean;
}

export default function ScrollReveal({
    children,
    mode = 'fade-up',
    delay = 0,
    duration = 0.6,
    className = '',
    threshold = 0.1,
    once = true,
    eager = false,
}: ScrollRevealProps) {
    return (
        <div
            data-scroll-reveal="out"
            data-scroll-reveal-once={once ? 'true' : 'false'}
            data-scroll-reveal-threshold={threshold}
            className={className}
            style={{
                opacity: eager ? 1 : 0,
                transform: HIDDEN_TRANSFORM[mode],
                filter: mode === 'blur' && !eager ? 'blur(10px)' : undefined,
                transition: [
                    `opacity ${duration}s ${EASE} ${delay}s`,
                    `transform ${duration}s ${EASE} ${delay}s`,
                    `filter ${duration}s ${EASE} ${delay}s`,
                ].join(', '),
                // Deliberately no `will-change`. Setting it on the hidden state forces
                // the browser to create a compositor layer per element, and with 110
                // instances that measured worse than leaving it off — Style & Layout
                // and TBT both rose. The browser promotes these on its own when the
                // transition actually runs.
            } as CSSProperties}
        >
            {children}
        </div>
    );
}
